import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppContext } from '../Store/Store';
import { useNavigate } from 'react-router-dom';

interface ManageTagProps {
  onNext?: () => void;
}

interface FilteredProduct {
  code: string;
  name: string;
  event_name?: string;
  brand_name?: string;
  client_name?: string;
  product_img_url?: string;
}

interface TokenDetails {
  exists: boolean;
  product?: string;
  redirect_url?: string;
  show_dropdown: boolean;
  show_input_field: boolean;
  show_serial_number_field: boolean;
  button_indication?: string;
  button_bg_color?: string;
  button_shadow_color?: string;
  input_indication?: string;
  selector_indication?: string;
  token_serial_number_indication?: string;
}

export default function ManageTag({ onNext }: ManageTagProps) {
  const {
    backendBaseUrl,
    token,
    setAppState,
    fetchData,
    setGlobalNotification,
    style,
  } = useAppContext();
  const navigate = useNavigate();
  const [productCode, setProductCode] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<FilteredProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<FilteredProduct[]>(
    []
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearchQuery, setDropdownSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] =
    useState<FilteredProduct | null>(null);
  const [tokenDetails, setTokenDetails] = useState<TokenDetails | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Compute button styling with fallback hierarchy
  const getButtonStyling = () => {
    // Default fallback values
    const fallbackStyling = {
      indication: 'Save Product',
      bgColor: 'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
      shadowColor: '#6991F8',
    };

    // If token has custom styling, use it
    if (
      tokenDetails?.button_indication ||
      tokenDetails?.button_bg_color ||
      tokenDetails?.button_shadow_color
    ) {
      return {
        indication:
          tokenDetails.button_indication || fallbackStyling.indication,
        bgColor: tokenDetails.button_bg_color || fallbackStyling.bgColor,
        shadowColor:
          tokenDetails.button_shadow_color || fallbackStyling.shadowColor,
      };
    }

    // If brand style has manage tag button styling, use it
    if (
      style &&
      (style.manage_tag_button_indication ||
        style.manage_tag_button_bg_color ||
        style.manage_tag_button_shadow_color)
    ) {
      return {
        indication:
          style.manage_tag_button_indication || fallbackStyling.indication,
        bgColor: style.manage_tag_button_bg_color || fallbackStyling.bgColor,
        shadowColor:
          style.manage_tag_button_shadow_color || fallbackStyling.shadowColor,
      };
    }

    // Use fallback styling
    return fallbackStyling;
  };

  // Compute input indication with fallback hierarchy
  const getInputIndication = () => {
    // Default fallback value
    const fallbackIndication = 'Enter product code to create new...';

    // If token has custom input indication, use it
    if (tokenDetails?.input_indication) {
      return tokenDetails.input_indication;
    }

    // If brand style has manage tag input indication, use it
    if (style?.manage_tag_input_indication) {
      return style.manage_tag_input_indication;
    }

    // Use fallback indication
    return fallbackIndication;
  };

  // Compute selector indication with fallback hierarchy
  const getSelectorIndication = () => {
    // Default fallback value
    const fallbackIndication = 'Select existing product...';

    // If token has custom selector indication, use it
    if (tokenDetails?.selector_indication) {
      return tokenDetails.selector_indication;
    }

    // If brand style has manage tag selector indication, use it
    if (style?.manage_tag_selector_indication) {
      return style.manage_tag_selector_indication;
    }

    // Use fallback indication
    return fallbackIndication;
  };

  // Compute serial number indication with fallback hierarchy
  const getSerialNumberIndication = () => {
    // Default fallback value
    const fallbackIndication = 'serial-number';

    // If token has custom serial number indication, use it
    if (tokenDetails?.token_serial_number_indication) {
      return tokenDetails.token_serial_number_indication;
    }

    // Use fallback indication
    return fallbackIndication;
  };

  const buttonStyling = getButtonStyling();
  const inputIndication = getInputIndication();
  const selectorIndication = getSelectorIndication();
  const serialNumberIndication = getSerialNumberIndication();

  // Fetch token details to get UI control flags
  useEffect(() => {
    const fetchTokenDetails = async () => {
      if (!token) return;

      try {
        const response = await axios.get(
          `${backendBaseUrl}/check-token/?token=${token}`
        );
        setTokenDetails(response.data);
      } catch (err) {
        console.error('Failed to fetch token details:', err);
        setError('Failed to validate token');
      }
    };

    fetchTokenDetails();
  }, [token, backendBaseUrl]);

  // Fetch filtered products when dropdown should be shown
  useEffect(() => {
    const fetchProducts = async () => {
      if (!token || !tokenDetails?.show_dropdown) return;

      setIsLoadingProducts(true);
      try {
        const response = await axios.get(
          `${backendBaseUrl}/token-filtered-products/?token=${token}&search=${dropdownSearchQuery}`
        );
        if (response.data.success) {
          setProducts(response.data.products);
          setFilteredProducts(response.data.products);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [token, tokenDetails?.show_dropdown, dropdownSearchQuery, backendBaseUrl]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownClick = () => {
    if (tokenDetails?.show_dropdown) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleDropdownSearch = (value: string) => {
    setDropdownSearchQuery(value);
  };

  const handleProductSelect = (product: FilteredProduct) => {
    setSelectedProduct(product);
    setProductCode(product.code);
    setShowDropdown(false);
    // Clear manual input when selecting from dropdown
    setDropdownSearchQuery('');
  };

  const handleManualInputChange = (value: string) => {
    setProductCode(value);
    // Clear dropdown selection when typing manually
    setSelectedProduct(null);
  };

  const handleSerialNumberChange = (value: string) => {
    if (value.length > 20) {
      alert('Serial number cannot exceed 20 characters');
      return;
    }
    setSerialNumber(value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!productCode) {
      alert('Product code is required!');
      return;
    }

    try {
      const response = await axios.post(`${backendBaseUrl}/assign-token/`, {
        token: token,
        product_code: productCode,
        serial_number: serialNumber || undefined,
      });
      setGlobalNotification(['Product created successfully!', 'success']);
      await fetchData();
      setAppState('redirect-product');
      if (onNext) {
        onNext();
      } else {
        navigate(`/?token=${token}`);
      }
    } catch (err) {
      console.error('Failed to assign token:', err);
      setError('Failed to update token');
    }
  };

  // Don't render anything until we have token details
  if (!tokenDetails) {
    return (
      <div
        className="main"
        style={{ position: 'relative', width: '100%', height: '100%' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div
      className="main"
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <div
        style={{
          position: 'absolute',
          top: '0%',
          height: '19%',
          width: '100%',
        }}
      ></div>
      <div
        className="review-actions"
        style={{
          position: 'absolute',
          top: '19%',
          height: '68%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          id="product-code-review"
          className="review-block"
          style={{
            height: '75.5%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              height: '70%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '5vh',
            }}
          >
            <form
              onSubmit={handleSubmit}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  alignItems: 'center',
                }}
              >
                {/* Manual Input Field */}
                {tokenDetails.show_input_field && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <input
                      type="text"
                      className="mail-input"
                      placeholder={inputIndication}
                      value={selectedProduct ? '' : productCode}
                      onChange={e => handleManualInputChange(e.target.value)}
                      disabled={!!selectedProduct}
                      style={{
                        height: '40px',
                        width: '80%',
                        maxWidth: '600px',
                        opacity: selectedProduct ? 0.5 : 1,
                        backgroundColor: selectedProduct ? '#f5f5f5' : 'white',
                        fontSize: '16px',
                        padding: '0 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                )}

                {/* OR separator */}
                {tokenDetails.show_input_field &&
                  tokenDetails.show_dropdown && (
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '16px',
                        margin: '5px 0',
                      }}
                    >
                      or
                    </div>
                  )}

                {/* Dropdown Selector */}
                {tokenDetails.show_dropdown && (
                  <div
                    style={{
                      position: 'relative',
                      width: '80%',
                      maxWidth: '600px',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                    ref={dropdownRef}
                  >
                    <div
                      onClick={handleDropdownClick}
                      style={{
                        height: '40px',
                        width: '100%',
                        maxWidth: '600px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        opacity: productCode && !selectedProduct ? 0.5 : 1,
                        fontSize: '16px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span
                        style={{
                          color: selectedProduct ? '#000' : '#666',
                          fontSize: '16px',
                        }}
                      >
                        {selectedProduct
                          ? `${selectedProduct.code} - ${selectedProduct.name}`
                          : selectorIndication}
                      </span>
                      <span style={{ color: '#666' }}>▼</span>
                    </div>

                    {showDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          width: '100%',
                          maxWidth: '600px',
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                      >
                        {/* Search input inside dropdown */}
                        <div
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Search products..."
                            value={dropdownSearchQuery}
                            onChange={e => handleDropdownSearch(e.target.value)}
                            style={{
                              width: '100%',
                              height: '30px',
                              border: '1px solid #ddd',
                              borderRadius: '3px',
                              padding: '0 8px',
                              fontSize: '16px',
                              boxSizing: 'border-box',
                            }}
                            autoFocus
                          />
                        </div>

                        {/* Products list */}
                        <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                          {isLoadingProducts ? (
                            <div
                              style={{
                                padding: '10px',
                                textAlign: 'center',
                                fontSize: '16px',
                              }}
                            >
                              Loading products...
                            </div>
                          ) : filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                              <div
                                key={product.code}
                                onClick={() => handleProductSelect(product)}
                                style={{
                                  padding: '10px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor:
                                    selectedProduct?.code === product.code
                                      ? '#f0f0f0'
                                      : 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                }}
                                onMouseEnter={e =>
                                  (e.currentTarget.style.backgroundColor =
                                    '#f5f5f5')
                                }
                                onMouseLeave={e =>
                                  (e.currentTarget.style.backgroundColor =
                                    selectedProduct?.code === product.code
                                      ? '#f0f0f0'
                                      : 'white')
                                }
                              >
                                {product.product_img_url ? (
                                  <img
                                    src={product.product_img_url}
                                    alt={product.name}
                                    style={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 6,
                                      objectFit: 'cover',
                                      marginRight: 8,
                                      border: '1px solid #eee',
                                      background: '#fafafa',
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 36,
                                      height: '36px',
                                      borderRadius: 6,
                                      background: '#eee',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginRight: 8,
                                    }}
                                  >
                                    <span
                                      role="img"
                                      aria-label="no image"
                                      style={{ fontSize: 18, color: '#bbb' }}
                                    >
                                      🖼️
                                    </span>
                                  </div>
                                )}
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontWeight: 'bold',
                                      fontSize: '16px',
                                    }}
                                  >
                                    {product.code}
                                  </div>
                                  <div
                                    style={{ fontSize: '14px', color: '#666' }}
                                  >
                                    {product.name}
                                  </div>
                                  {(product.event_name ||
                                    product.brand_name ||
                                    product.client_name) && (
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#999',
                                      }}
                                    >
                                      {[
                                        product.client_name,
                                        product.brand_name,
                                        product.event_name,
                                      ]
                                        .filter(Boolean)
                                        .join(' > ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div
                              style={{
                                padding: '10px',
                                textAlign: 'center',
                                color: '#666',
                                fontSize: '16px',
                              }}
                            >
                              No products found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Clear Selection Button */}
                {selectedProduct && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedProduct(null);
                        setProductCode('');
                        setSerialNumber('');
                      }}
                      style={{
                        height: '30px',
                        padding: '0 15px',
                        fontSize: '14px',
                        color: '#666',
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Clear Selection
                    </button>
                  </div>
                )}

                {/* Serial Number Field */}
                {tokenDetails.show_serial_number_field &&
                  (selectedProduct || productCode) && (
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <input
                        type="text"
                        className="mail-input"
                        placeholder={serialNumberIndication}
                        value={serialNumber}
                        onChange={e => handleSerialNumberChange(e.target.value)}
                        style={{
                          height: '40px',
                          width: '80%',
                          maxWidth: '600px',
                          opacity: 1,
                          backgroundColor: 'white',
                          fontSize: '16px',
                          padding: '0 12px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}
              </div>

              <button
                type="submit"
                style={{
                  height: '40px',
                  marginTop: '25px',
                  width: '200px',
                  borderRadius: '7px',
                  padding: '0 20px',
                  fontSize: '90%',
                  fontWeight: 'bold',
                  color: 'white',
                  background: buttonStyling.bgColor,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: `0 5px 5px ${buttonStyling.shadowColor}`,
                }}
              >
                {buttonStyling.indication}
              </button>
            </form>
          </div>
          {error && (
            <div
              className="error-message"
              style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}
            >
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
